import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, formatApiErrorDetail } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import {
  ArrowLeft, Plus, Trash2, Sparkles, Star, Target, MessageSquare,
  Users, Save, Loader, AlertCircle, FileText, Check, X,
  Heart, Calendar, Briefcase, Building2,
} from "lucide-react";

const STATUS_OPTIONS = [
  { value: "rejected", label: "Rejected", color: "#94A3B8" },
  { value: "no_response", label: "No response", color: "#FFC145" },
  { value: "withdrew", label: "I withdrew", color: "#0F1B2D" },
  { value: "still_interviewing", label: "Still interviewing", color: "#20C1A7" },
  { value: "offer", label: "Got an offer!", color: "#10B981" },
];

function relTime(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso);
    const d = (Date.now() - t.getTime()) / 1000;
    if (d < 60) return "just now";
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
    return t.toLocaleDateString();
  } catch {
    return "—";
  }
}

/* ------------------------------------------------------------------ */
/*  Soft Resmo mascot — "I'm here for you" treatment                   */
/* ------------------------------------------------------------------ */
function SoftResmo({ size = 110 }) {
  const { mode } = useTheme();
  if (mode === "promo") return null;
  return (
    <img
      src="/brand/resmo-character.png"
      alt="Resmo, cheering you on"
      style={{
        width: size,
        height: "auto",
        filter: "drop-shadow(0 6px 18px rgba(15,27,45,0.18))",
        // Slight desaturation gives Resmo a softer, more reflective vibe than
        // the usual full-vibrancy hype mode — same character, gentler energy.
        WebkitFilter: "drop-shadow(0 6px 18px rgba(15,27,45,0.18))",
      }}
      draggable={false}
    />
  );
}

/* ================================================================== */
/*  LIST mode — "Stepping Stones — your growth log"                    */
/* ================================================================== */
export function SteppingStonesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { mode } = useTheme();
  const isPro = mode === "promo";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stepping-stones");
      setItems(data || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!window.confirm(`Remove the entry for ${s.role} at ${s.company}?`)) return;
    try {
      await api.delete(`/stepping-stones/${s.id}`);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">

        <button onClick={() => navigate("/resources")} className="rm-tag mb-3" data-testid="stones-back">
          <ArrowLeft size={14} /> Back to Resources
        </button>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8 items-center">
          <div className="md:col-span-9">
            <div className="rm-tag mb-3">
              <Heart size={12} /> Reflect · Reframe · Rebuild
            </div>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-tight mb-2">
              Stepping <span className="rm-text-orange">stones.</span>
            </h1>
            <p className="text-base md:text-lg max-w-2xl" style={{ color: "#475569" }}>
              Every interview teaches you something — even the ones that don't end in an
              offer. Capture the wins, the lessons, and the people you met. This is your
              growth log on the way to {isPro ? "the role" : "the dream job"}.
            </p>
          </div>
          <div className="md:col-span-3 flex justify-end">
            <SoftResmo size={130} />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-8">
          <button
            onClick={() => navigate("/stepping-stones/new")}
            className="rm-btn rm-btn-primary"
            data-testid="stones-add-new"
          >
            <Plus size={16} /> New stepping stone
          </button>
          <button
            onClick={() => navigate("/resources/interview/new")}
            className="rm-btn rm-btn-ghost"
            data-testid="stones-link-interview"
          >
            <Sparkles size={14} /> Generate interview prep
          </button>
        </div>

        {error && (
          <div className="p-3 mb-6 rounded-md font-mono-rm text-sm flex items-start gap-2"
               style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}>
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 font-mono-rm" data-testid="stones-loading">
            <Loader size={24} className="animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div
            className="rm-card-static p-8 md:p-10 text-center"
            data-testid="stones-empty"
            style={{ background: "#FFFBEB" }}
          >
            <SoftResmo size={120} />
            <h2 className="font-display text-2xl uppercase mt-4 mb-2">
              No stones yet. <span className="rm-text-orange">That's okay.</span>
            </h2>
            <p className="text-base mb-5 max-w-md mx-auto" style={{ color: "#475569" }}>
              When you come out of an interview — good, bad, or in-between — drop the
              highlights here. Future-you will thank you for it.
            </p>
            <button
              onClick={() => navigate("/stepping-stones/new")}
              className="rm-btn rm-btn-primary"
              data-testid="stones-empty-cta"
            >
              <Plus size={16} /> Add your first
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="stones-list">
            {items.map((s) => {
              const statusMeta = STATUS_OPTIONS.find((o) => o.value === s.status) || STATUS_OPTIONS[0];
              return (
                <div
                  key={s.id}
                  className="rm-card p-5 md:p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
                  data-testid={`stone-card-${s.id}`}
                >
                  <div className="md:col-span-7 cursor-pointer"
                       onClick={() => navigate(`/stepping-stones/${s.id}`)}>
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span
                        className="rm-tag text-[10px]"
                        style={{ background: statusMeta.color, color: "white", borderColor: "#0F1B2D" }}
                      >
                        {statusMeta.label}
                      </span>
                      {s.interview_date && (
                        <span className="font-mono-rm text-[10px] uppercase tracking-widest"
                              style={{ color: "#475569" }}>
                          <Calendar size={10} className="inline" /> {s.interview_date}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-xl uppercase leading-tight">{s.role}</h3>
                    <div className="font-mono-rm text-sm mt-0.5" style={{ color: "#475569" }}>
                      <Building2 size={12} className="inline mr-1" /> {s.company}
                    </div>
                  </div>

                  <div className="md:col-span-3 font-mono-rm text-[11px]" style={{ color: "#475569" }}>
                    <div><Star size={11} className="inline rm-text-orange" /> {(s.wins || []).length} wins logged</div>
                    <div><Target size={11} className="inline" /> {(s.focus_areas || []).length} focus areas</div>
                    <div><Users size={11} className="inline" /> {(s.contacts || []).length} contacts saved</div>
                    <div className="mt-1.5" style={{ color: "#94A3B8" }}>
                      Logged {relTime(s.created_at)}
                    </div>
                  </div>

                  <div className="md:col-span-2 flex md:flex-col gap-2 md:items-end">
                    <button
                      onClick={() => navigate(`/stepping-stones/${s.id}`)}
                      className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                      data-testid={`stone-open-${s.id}`}
                    >
                      Open →
                    </button>
                    <button
                      onClick={() => remove(s)}
                      className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                      style={{ color: "#991B1B" }}
                      data-testid={`stone-delete-${s.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  EDITOR mode — shared create + edit form                            */
/* ================================================================== */
function emptyDraft() {
  return {
    role: "",
    company: "",
    interview_date: "",
    status: "rejected",
    wins: [""],
    focus_areas: [""],
    interviewer_feedback: "",
    contacts: [],
    notes: "",
  };
}

function emptyContact() {
  return { name: "", role: "", email: "", linkedin: "", notes: "" };
}

export function SteppingStoneEditor({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [draft, setDraft] = useState(emptyDraft());
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (mode !== "edit" || !id) return;
    (async () => {
      try {
        const { data } = await api.get(`/stepping-stones/${id}`);
        setDraft({
          ...emptyDraft(),
          ...data,
          wins: data.wins?.length ? data.wins : [""],
          focus_areas: data.focus_areas?.length ? data.focus_areas : [""],
          contacts: data.contacts || [],
        });
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, id]);

  const updateField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const updateListItem = (k, i, v) => setDraft((d) => {
    const next = [...(d[k] || [])];
    next[i] = v;
    return { ...d, [k]: next };
  });
  const addListItem = (k) => setDraft((d) => ({ ...d, [k]: [...(d[k] || []), ""] }));
  const removeListItem = (k, i) => setDraft((d) => {
    const next = [...(d[k] || [])];
    next.splice(i, 1);
    return { ...d, [k]: next.length === 0 ? [""] : next };
  });

  const updateContact = (i, field, v) => setDraft((d) => {
    const next = [...(d.contacts || [])];
    next[i] = { ...next[i], [field]: v };
    return { ...d, contacts: next };
  });
  const addContact = () => setDraft((d) => ({ ...d, contacts: [...(d.contacts || []), emptyContact()] }));
  const removeContact = (i) => setDraft((d) => {
    const next = [...(d.contacts || [])];
    next.splice(i, 1);
    return { ...d, contacts: next };
  });

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!draft.role.trim() || !draft.company.trim()) {
      setError("Please add at least the role and company.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        wins: (draft.wins || []).filter((s) => s.trim()),
        focus_areas: (draft.focus_areas || []).filter((s) => s.trim()),
      };
      let result;
      if (mode === "edit" && id) {
        const { data } = await api.patch(`/stepping-stones/${id}`, payload);
        result = data;
      } else {
        const { data } = await api.post("/stepping-stones", payload);
        result = data;
      }
      setSaved(true);
      setTimeout(() => {
        navigate(`/stepping-stones/${result.id}`);
      }, 600);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Loader size={24} className="animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">

        <button
          onClick={() => navigate("/stepping-stones")}
          className="rm-tag mb-3"
          data-testid="stone-editor-back"
        >
          <ArrowLeft size={14} /> Back to stepping stones
        </button>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-center mb-7">
          <div className="md:col-span-9">
            <div className="rm-tag mb-2">
              <Heart size={12} /> {mode === "edit" ? "Edit reflection" : "New reflection"}
            </div>
            <h1 className="font-display text-3xl md:text-4xl uppercase leading-tight">
              {mode === "edit" ? (
                <>Tune the <span className="rm-text-orange">stone.</span></>
              ) : (
                <>Drop a <span className="rm-text-orange">stone.</span></>
              )}
            </h1>
            <p className="text-sm md:text-base mt-1.5" style={{ color: "#475569" }}>
              Setbacks aren't stop signs — they're stepping stones. Capture what mattered.
            </p>
          </div>
          <div className="md:col-span-3 flex justify-end">
            <SoftResmo size={110} />
          </div>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="stone-editor-form">

          {/* Role + company + date + status */}
          <section className="rm-card-static p-5 md:p-6 space-y-4">
            <div className="rm-label flex items-center gap-2">
              <Briefcase size={14} /> The interview
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="rm-label block mb-1.5">Role *</label>
                <input
                  type="text"
                  value={draft.role}
                  onChange={(e) => updateField("role", e.target.value)}
                  placeholder="Senior Product Manager"
                  className="w-full px-3 py-2.5 font-mono-rm text-sm"
                  style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                  data-testid="stone-role-input"
                />
              </div>
              <div>
                <label className="rm-label block mb-1.5">Company *</label>
                <input
                  type="text"
                  value={draft.company}
                  onChange={(e) => updateField("company", e.target.value)}
                  placeholder="Acme Corp"
                  className="w-full px-3 py-2.5 font-mono-rm text-sm"
                  style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                  data-testid="stone-company-input"
                />
              </div>
              <div>
                <label className="rm-label block mb-1.5">Interview date</label>
                <input
                  type="text"
                  value={draft.interview_date}
                  onChange={(e) => updateField("interview_date", e.target.value)}
                  placeholder="Apr 12, 2026"
                  className="w-full px-3 py-2.5 font-mono-rm text-sm"
                  style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                  data-testid="stone-date-input"
                />
              </div>
              <div>
                <label className="rm-label block mb-1.5">Outcome</label>
                <select
                  value={draft.status}
                  onChange={(e) => updateField("status", e.target.value)}
                  className="w-full px-3 py-2.5 font-mono-rm text-sm"
                  style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6 }}
                  data-testid="stone-status-select"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Wins */}
          <ListSection
            title="Things I did really well"
            subtitle="Even if it didn't pan out — what are you proud of?"
            icon={<Star size={14} className="rm-text-orange" />}
            items={draft.wins}
            onChange={(i, v) => updateListItem("wins", i, v)}
            onAdd={() => addListItem("wins")}
            onRemove={(i) => removeListItem("wins", i)}
            placeholder="Nailed the system-design walkthrough"
            testIdPrefix="stone-win"
            background="#FFFBEB"
          />

          {/* Focus areas */}
          <ListSection
            title="What I'll focus on next time"
            subtitle="Lessons, not regrets. Be specific and forgiving."
            icon={<Target size={14} />}
            items={draft.focus_areas}
            onChange={(i, v) => updateListItem("focus_areas", i, v)}
            onAdd={() => addListItem("focus_areas")}
            onRemove={(i) => removeListItem("focus_areas", i)}
            placeholder="Practice 2-minute STAR stories for trade-off questions"
            testIdPrefix="stone-focus"
            background="#F1F5F9"
          />

          {/* Interviewer feedback */}
          <section className="rm-card-static p-5 md:p-6">
            <div className="rm-label flex items-center gap-2 mb-2">
              <MessageSquare size={14} /> Anything the interviewers told you
            </div>
            <p className="text-xs mb-3" style={{ color: "#475569" }}>
              Direct feedback, hints, or moments where you felt the room shift. Useful later.
            </p>
            <textarea
              value={draft.interviewer_feedback}
              onChange={(e) => updateField("interviewer_feedback", e.target.value)}
              placeholder="They mentioned they were looking for someone with more cross-functional roadmap ownership..."
              rows={4}
              className="w-full px-3 py-2.5 font-mono-rm text-sm"
              style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6, resize: "vertical" }}
              data-testid="stone-feedback-input"
            />
          </section>

          {/* Contacts */}
          <section className="rm-card-static p-5 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="rm-label flex items-center gap-2">
                <Users size={14} /> People to stay in touch with
              </div>
              <button
                type="button"
                onClick={addContact}
                className="rm-btn rm-btn-ghost text-xs px-3 py-1.5"
                data-testid="stone-add-contact"
              >
                <Plus size={12} /> Add contact
              </button>
            </div>
            {(draft.contacts || []).length === 0 ? (
              <p className="text-xs italic" style={{ color: "#94A3B8" }}>
                No contacts yet. Add interviewers, recruiters, or referrers worth reaching back out to.
              </p>
            ) : (
              <div className="space-y-3" data-testid="stone-contacts-list">
                {draft.contacts.map((c, i) => (
                  <div
                    key={i}
                    className="p-4 rounded-md grid grid-cols-1 md:grid-cols-2 gap-3"
                    style={{ background: "#F8FAFC", border: "2px solid #E2E8F0" }}
                    data-testid={`stone-contact-${i}`}
                  >
                    <input
                      value={c.name}
                      onChange={(e) => updateContact(i, "name", e.target.value)}
                      placeholder="Name"
                      className="px-3 py-2 font-mono-rm text-sm"
                      style={{ background: "white", border: "2px solid #0F1B2D", borderRadius: 5 }}
                    />
                    <input
                      value={c.role}
                      onChange={(e) => updateContact(i, "role", e.target.value)}
                      placeholder="Role (e.g. Hiring Manager)"
                      className="px-3 py-2 font-mono-rm text-sm"
                      style={{ background: "white", border: "2px solid #0F1B2D", borderRadius: 5 }}
                    />
                    <input
                      value={c.email}
                      onChange={(e) => updateContact(i, "email", e.target.value)}
                      placeholder="Email"
                      className="px-3 py-2 font-mono-rm text-sm"
                      style={{ background: "white", border: "2px solid #0F1B2D", borderRadius: 5 }}
                    />
                    <input
                      value={c.linkedin}
                      onChange={(e) => updateContact(i, "linkedin", e.target.value)}
                      placeholder="LinkedIn URL"
                      className="px-3 py-2 font-mono-rm text-sm"
                      style={{ background: "white", border: "2px solid #0F1B2D", borderRadius: 5 }}
                    />
                    <input
                      value={c.notes}
                      onChange={(e) => updateContact(i, "notes", e.target.value)}
                      placeholder="Notes (e.g. ‘felt warm in the loop — follow up Q3’)"
                      className="md:col-span-2 px-3 py-2 font-mono-rm text-sm"
                      style={{ background: "white", border: "2px solid #0F1B2D", borderRadius: 5 }}
                    />
                    <div className="md:col-span-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeContact(i)}
                        className="rm-btn rm-btn-ghost text-xs px-3 py-1.5"
                        style={{ color: "#991B1B" }}
                        data-testid={`stone-remove-contact-${i}`}
                      >
                        <X size={12} /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Free notes */}
          <section className="rm-card-static p-5 md:p-6">
            <div className="rm-label flex items-center gap-2 mb-2">
              <FileText size={14} /> Anything else
            </div>
            <textarea
              value={draft.notes}
              onChange={(e) => updateField("notes", e.target.value)}
              placeholder="Free-form thoughts — what you'd say to past-you, what you'd change, what you're grateful for."
              rows={5}
              className="w-full px-3 py-2.5 font-mono-rm text-sm"
              style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 6, resize: "vertical" }}
              data-testid="stone-notes-input"
            />
          </section>

          {error && (
            <div
              className="p-3 rounded-md font-mono-rm text-sm flex items-start gap-2"
              style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
              data-testid="stone-editor-error"
            >
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rm-btn rm-btn-primary"
              data-testid="stone-save-btn"
            >
              {saving ? <Loader size={16} className="animate-spin" /> : saved ? <Check size={16} /> : <Save size={16} />}
              {saved ? "Saved" : mode === "edit" ? "Save changes" : "Drop this stone"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/stepping-stones")}
              className="rm-btn rm-btn-ghost"
              data-testid="stone-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------- ListSection helper ------- */
function ListSection({ title, subtitle, icon, items, onChange, onAdd, onRemove, placeholder, testIdPrefix, background }) {
  return (
    <section className="rm-card-static p-5 md:p-6" style={{ background }}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <div className="rm-label flex items-center gap-2">{icon} {title}</div>
          {subtitle && (
            <p className="text-xs mt-1" style={{ color: "#475569" }}>{subtitle}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="rm-btn rm-btn-ghost text-xs px-3 py-1.5"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus size={12} /> Add
        </button>
      </div>
      <div className="space-y-2 mt-3">
        {(items || []).map((v, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`${testIdPrefix}-row-${i}`}>
            <span className="rm-text-orange font-display text-lg leading-none w-4 flex-shrink-0">▸</span>
            <input
              type="text"
              value={v}
              onChange={(e) => onChange(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 font-mono-rm text-sm"
              style={{ background: "white", border: "2px solid #0F1B2D", borderRadius: 5 }}
              data-testid={`${testIdPrefix}-input-${i}`}
            />
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="text-[11px] px-2 py-1.5"
              style={{ color: "#94A3B8" }}
              aria-label="Remove"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ================================================================== */
/*  DETAIL mode — read-only view with "edit" toggle                    */
/* ================================================================== */
export function SteppingStoneDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [stone, setStone] = useState(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/stepping-stones/${id}`);
        setStone(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  if (editing) return <SteppingStoneEditor mode="edit" />;

  if (!stone && !error) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Loader size={24} className="animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-10">
          <div className="rm-card-static p-6">{error}</div>
        </div>
      </div>
    );
  }

  const statusMeta = STATUS_OPTIONS.find((o) => o.value === stone.status) || STATUS_OPTIONS[0];

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="stone-detail">
        <button
          onClick={() => navigate("/stepping-stones")}
          className="rm-tag mb-3"
          data-testid="stone-detail-back"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="rm-card-static p-6 md:p-8 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <span className="rm-tag text-xs"
                    style={{ background: statusMeta.color, color: "white", borderColor: "#0F1B2D" }}>
                {statusMeta.label}
              </span>
              <h1 className="font-display text-3xl md:text-4xl uppercase leading-tight mt-2">
                {stone.role}
              </h1>
              <div className="font-mono-rm text-sm mt-1" style={{ color: "#475569" }}>
                <Building2 size={12} className="inline mr-1" /> {stone.company}
                {stone.interview_date && <> · <Calendar size={11} className="inline" /> {stone.interview_date}</>}
              </div>
            </div>
            <button
              onClick={() => setEditing(true)}
              className="rm-btn rm-btn-primary"
              data-testid="stone-detail-edit"
            >
              Edit
            </button>
          </div>
        </div>

        {(stone.wins || []).length > 0 && (
          <DetailSection
            icon={<Star size={16} className="rm-text-orange" />}
            title="Wins"
            background="#FFFBEB"
          >
            <ul className="space-y-2 text-sm">
              {stone.wins.map((w, i) => (
                <li key={i}><span className="rm-text-orange">▸</span> {w}</li>
              ))}
            </ul>
          </DetailSection>
        )}

        {(stone.focus_areas || []).length > 0 && (
          <DetailSection
            icon={<Target size={16} />}
            title="Focus for next time"
            background="#F1F5F9"
          >
            <ul className="space-y-2 text-sm">
              {stone.focus_areas.map((f, i) => (
                <li key={i}><span className="rm-text-orange">▸</span> {f}</li>
              ))}
            </ul>
          </DetailSection>
        )}

        {stone.interviewer_feedback && (
          <DetailSection
            icon={<MessageSquare size={16} />}
            title="Interviewer feedback"
          >
            <p className="text-sm whitespace-pre-wrap">{stone.interviewer_feedback}</p>
          </DetailSection>
        )}

        {(stone.contacts || []).length > 0 && (
          <DetailSection
            icon={<Users size={16} />}
            title="People to stay in touch with"
          >
            <div className="space-y-3">
              {stone.contacts.map((c, i) => (
                <div key={i} className="p-3 rounded-md"
                     style={{ background: "#F8FAFC", border: "2px solid #E2E8F0" }}>
                  <div className="font-bold">{c.name || "—"}</div>
                  {c.role && <div className="text-xs" style={{ color: "#475569" }}>{c.role}</div>}
                  <div className="flex flex-wrap gap-3 mt-1.5 text-xs font-mono-rm">
                    {c.email && <span style={{ color: "#0F1B2D" }}>{c.email}</span>}
                    {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="rm-text-orange">LinkedIn</a>}
                  </div>
                  {c.notes && <div className="text-xs italic mt-1.5" style={{ color: "#475569" }}>{c.notes}</div>}
                </div>
              ))}
            </div>
          </DetailSection>
        )}

        {stone.notes && (
          <DetailSection
            icon={<FileText size={16} />}
            title="Notes"
          >
            <p className="text-sm whitespace-pre-wrap">{stone.notes}</p>
          </DetailSection>
        )}

        <div className="font-mono-rm text-xs uppercase tracking-widest text-center mt-8"
             style={{ color: "#475569" }}>
          Stepping stone · logged {relTime(stone.created_at)} · keep going 🟧
        </div>
      </div>
    </div>
  );
}

function DetailSection({ icon, title, children, background = "white" }) {
  return (
    <section className="rm-card-static p-5 md:p-6 mb-4" style={{ background }}>
      <div className="rm-label flex items-center gap-2 mb-3">{icon} {title}</div>
      {children}
    </section>
  );
}

/* ================================================================== */
/*  Router wrapper — picks the mode                                    */
/* ================================================================== */
export default function SteppingStones({ mode }) {
  if (mode === "list") return <SteppingStonesList />;
  if (mode === "new") return <SteppingStoneEditor mode="new" />;
  if (mode === "detail") return <SteppingStoneDetail />;
  return <SteppingStonesList />;
}
